import { getAccountCurrency, salesChannelMapping } from "./commons";
import { setBundleOfProductItems } from "Store/contentState/marketplaceSlice";
import OOT_API from "Constants/services/oot";

export const TYPE_OF_BUNDLE = "Bundle";
export const MXIE_BUNDLE = "MXIE";
export const SHOW_DURATION_ENABLED = 'yes';

export const BUNDLE_COLUMN_DEFS = {
    [MXIE_BUNDLE.toLowerCase()]: [{
        headerName: "Sales item name",
        field: "product_description",
        flex: 1
    }, {
        headerName: "Code",
        field: "product_id",
        flex: 1
    }, {
        headerName: "Quantity",
        field: "quantity",
        flex: 1
    }],
    [TYPE_OF_BUNDLE.toLowerCase()]: [{
        headerName: `${TYPE_OF_BUNDLE} package`,
        field: "product_description",
        id:0
    }, {
        headerName: "Sales items included",
        field: "product_id",
         id:1
    }, {
        headerName: "Quantity",
        field: "quantity",
         id:2
    }]
}

/**
 * 
 * @param { marketplaceSelector } 
 * @returns 
 */
export const getCreateSubmissionData = ({ marketplaceSelector, productOverride = null }) => {
    const accountDetails = marketplaceSelector?.accountDetails || [];
    const productBundle = marketplaceSelector.productBundle;
    const product = productOverride || productBundle.product_details;
    const type_of_bundle = product?.type_of_bundle?.toLowerCase() || TYPE_OF_BUNDLE?.toLowerCase();
    // const mxie_required = product?.mxie_required === 'yes' ? true : false;
    const mxie_required = product?.mxie_required && product?.mxie_required?.toLowerCase() === 'yes' ? true : false;
    const productWithSelectedCompactIdType = marketplaceSelector.products.find((item) => item.id === product?.id);
    const compact_id_type = productWithSelectedCompactIdType?.kit_products?.find(p => p.checked);

    const currency = getAccountCurrency(accountDetails[0]['currency'], accountDetails[0]['currencyisocode']);
    const salesChannelMap = salesChannelMapping();
    const selectedEdgeMxieServer = productBundle?.selectedEdgeMxieServer;
    const sales_channel = salesChannelMap[accountDetails[0]['account_type']] || "";
    if (!sales_channel || !product?.id) {
        return null;
    }
    return {
        id: product?.id,
        accountId: accountDetails[0].id,
        currency,
        detailType: type_of_bundle,
        compact_id_type: compact_id_type?.name?.toLowerCase(),
        salesChannel: sales_channel,
        productQty: product?.product_quantity,
        configurator_app_name: product?.configurator_app_name,
        //contract_duration_months: Number(product?.selectedDuration) || 1,
        contract_duration_months: getBundleDurationMonths(product),
        selectedEdgeMxieServer,
        mxie_required,
    }
}

/**
 * 
 * @param components 
 * @param configuratorSaleItems 
 * @returns 
 */
const normalizeProductCode = (value: unknown) => String(value || '').trim().toLowerCase();

const saleItemMatchesComponent = (saleItem: any, component: any) => {
    const productCode = normalizeProductCode(saleItem?.productCode);
    if (!productCode) return false;

    return [component?.product_id, component?.id]
        .map(normalizeProductCode)
        .some((candidate) => candidate && candidate === productCode);
};

const resolveBundleUnitPrice = (components: any[], product: any) => {
    const componentTotal = components.reduce(
        (total, component) => total + (component.product_total_cost || 0),
        0
    );
    if (componentTotal > 0) {
        return componentTotal;
    }

    const catalogPrice = Number(product?.price_amount ?? product?.grouped_price_amount ?? 0);
    return Number.isFinite(catalogPrice) ? catalogPrice : 0;
};

const buildCatalogProductMap = (catalogProducts: any[] = []) => {
    const catalogById = new Map<string, any>();
    catalogProducts.forEach((product) => {
        if (product?.id != null) {
            catalogById.set(String(product.id), product);
        }
    });
    return catalogById;
};

const getCatalogMatch = (catalogById: Map<string, any>, productCode: unknown) =>
    catalogById.get(String(productCode ?? ''));

const enrichLineWithCatalog = (line: any, catalogById: Map<string, any>) => {
    if (!line) {
        return line;
    }

    const catalogMatch = getCatalogMatch(catalogById, line.product_id ?? line.id);
    if (!catalogMatch) {
        return line;
    }

    return {
        ...line,
        product_type_id: catalogMatch.product_type_id || line.product_type_id,
        is_agent_model: catalogMatch.is_agent_model ?? line.is_agent_model ?? 0,
    };
};

export const updateComponentPriceByConfiguratorSaleItems = (
    components,
    configuratorSaleItems,
    catalogById: Map<string, any> = new Map(),
) => {
    if (!components) return [];
    if (!configuratorSaleItems) {
        if (!catalogById.size) {
            return components;
        }
        return components.map((component) => enrichLineWithCatalog(component, catalogById));
    }

    return components.map(component => {
        const matchedItem = configuratorSaleItems.find((item: any) => saleItemMatchesComponent(item, component));
        const enrichedComponent = enrichLineWithCatalog(component, catalogById);

        if (!matchedItem) {
            return enrichedComponent;
        }

        return enrichLineWithCatalog({
            ...enrichedComponent,
            price_amount: Number(matchedItem["IRP"] || enrichedComponent.price_amount),
            product_total_cost: Number(matchedItem["totalIRP"]),
            quantity: Number(matchedItem["quantity"]),
            product_type_id: enrichedComponent.product_type_id || matchedItem?.ProductType,
        }, catalogById);
    });
}


/**
 * 
 * @param data 
 */
export const updateProductTotalPriceAndAdditionalItems = async (data,marketplaceSelector) => {
    const { product, configuratorSaleItems, dispatch, bundleComponents, type_of_bundle } = data;
    const accountDetails = marketplaceSelector?.accountDetails || [];
    const currency = getAccountCurrency(accountDetails[0]['currency'], accountDetails[0]['currencyisocode']);
    const catalogProductCodes = [
        ...configuratorSaleItems.map((item) => item.productCode),
        ...(bundleComponents || []).map((component) => component.product_id),
    ].filter(Boolean);
    const productInfo = catalogProductCodes.length
        ? await OOT_API.getSpecificProductData(accountDetails[0].id, currency, catalogProductCodes.join(','))
        : { data: [] };
    const catalogById = buildCatalogProductMap(productInfo?.data || []);
    let components = updateComponentPriceByConfiguratorSaleItems(bundleComponents, configuratorSaleItems, catalogById);
    if (type_of_bundle === TYPE_OF_BUNDLE.toLowerCase()) {
        const unitPrice = resolveBundleUnitPrice(components, product);
        const productDetails = { ...product, price_amount: unitPrice, product_total_cost: unitPrice };
        dispatch(setBundleOfProductItems({ key: 'product_details', value: productDetails }));
    } else {
        const productDetails = { ...product, product_total_cost: product.price_amount * product.product_quantity };
        dispatch(setBundleOfProductItems({ key: 'product_details', value: productDetails }));
    }

    const additionalItems = [];

    for (const saleItem of configuratorSaleItems) {
        const matchedItem = bundleComponents.find((item) => saleItemMatchesComponent(saleItem, item));
        if (matchedItem) continue;

        const catalogMatch = getCatalogMatch(catalogById, saleItem.productCode);

        additionalItems.push({
            quantity: +saleItem.quantity,
            product_id: saleItem.productCode,
            price_amount: saleItem.IRP || 0,
            is_agent_model: catalogMatch?.is_agent_model ?? 0,
            product_description: saleItem?.workItem || '',
            id: saleItem.productCode,
            is_deletable: false,
            is_dependent_item: true,
            parent_product_id: product.id,
            product_total_cost: saleItem.totalIRP || 0,
            duration: saleItem?.duration || 1,
            productTitle: saleItem.productTitle,
            product_type_id: catalogMatch?.product_type_id || saleItem?.ProductType,
        });
    }

    dispatch(setBundleOfProductItems({ key: 'additionalItems', value: additionalItems }));
    dispatch(setBundleOfProductItems({ key: 'bundleComponents', value: components }));

    return {
        bundleComponents: components
    }
}

/**
 * 
 * @param product 
 * @returns 
 */
export const createSynceOfferPayloadForCrosser = (product) => {
    if (!product?.is_bundled_product || product?.type_of_bundle !== TYPE_OF_BUNDLE.toLowerCase() || !product?.bundleComponents) {
        return {};
    }
    
    const selectedKitType = product?.kit_products?.find(kit => kit?.checked);

    return {
        compact_id: product?.id,
        compact_id_type: selectedKitType?.name,
        type: "bundled_product",
        // products: bundleComponents,
        product_code: "", // replace the product code with empty string for bundle,
        //duration: Number(product?.selectedDuration) || 1,
        duration: getBundleDurationMonths(product),
    };
}

export const BUNDLE_API_ERROR_MESSAGE = {
    fetchBundleDetails: 'Something went wrong, Failed to get product details. Please try again later.',
}

/** Normalizes Yes/No enum values from API (Yes, YES, yes, etc.). */
export const normalizeYesNoFlag = (value: unknown): string => {
  if (value == null || value === '') {
    return '';
  }
  return String(value).trim().toLowerCase();
};
/** Returns true for Yes/No enum values that mean enabled. */
export const isYesNoEnabled = (value: unknown): boolean =>
  normalizeYesNoFlag(value) === SHOW_DURATION_ENABLED;
/**
 * Returns show_duration from the first kit_products entry.
 * Precedence: raw component row [0] (has product_id) → grouped package [0] → ''.
 * Does NOT use kit_products.find(kit => kit.checked).
 */
const getFirstKitShowDuration = (product): string => {
  const kits = product?.kit_products ?? [];
  if (!kits.length) {
    return '';
  }
  const firstKit = kits[0];
  return normalizeYesNoFlag(firstKit?.show_duration);
};
/**
 * Authoritative bundle-level show_duration.
 * Precedence: kit_products[0] → product.show_duration (backend flatten) → ''.
 * kit_products[0] always wins when present (fixes Test 3 in AI review).
 */
export const resolveAuthoritativeShowDuration = (product): string => {
  const fromFirstKit = getFirstKitShowDuration(product);
  if (fromFirstKit) {
    return fromFirstKit;
  }
  return normalizeYesNoFlag(product?.show_duration);
};
/** @deprecated Use resolveAuthoritativeShowDuration — kept for test compatibility */
export const resolveShowDuration = resolveAuthoritativeShowDuration;
export const isBundleDurationVisible = (product): boolean =>
  product?.type_of_bundle?.toLowerCase() === TYPE_OF_BUNDLE.toLowerCase()
  && isYesNoEnabled(resolveAuthoritativeShowDuration(product));
export const getBundleDurationMonths = (product) => {
  const duration = Number(product?.selectedDuration ?? product?.minimum_duration);
  return isBundleDurationVisible(product)
    ? (Number.isFinite(duration) ? duration : 12)
    : 1;
};

/** Enriches bundle additional items with catalog product_type_id before add-to-cart. */
export const enrichBundleAdditionalItemsForCart = async (
  items: any[] = [],
  account?: { id: string; currency?: string; currencyisocode?: string } | null,
) => {
  if (!items.length || !account) {
    return items;
  }

  const missingTypeIds = items
    .filter((item) => !item?.product_type_id && item?.product_id)
    .map((item) => item.product_id);

  if (!missingTypeIds.length) {
    return items;
  }

  try {
    const currency = getAccountCurrency(account.currency, account.currencyisocode);
    const productInfo = await OOT_API.getSpecificProductData(
      account.id,
      currency,
      missingTypeIds.join(','),
    );

    return items.map((item) => {
      const matchedProductInfo = productInfo?.data?.find(
        (info) => String(info.id) === String(item.product_id),
      );

      return {
        ...item,
        product_type_id: item.product_type_id || matchedProductInfo?.product_type_id,
        is_agent_model: item.is_agent_model ?? matchedProductInfo?.is_agent_model ?? 0,
      };
    });
  } catch (error) {
    console.error('Failed to enrich bundle additional items for cart:', error);
    return items;
  }
};
